<template>
  <div>
    <section>
      <b-col class="d-flex justify-content-end px-0 pt-25">
        <filter-wrapper
          :properties="selectedFilters"
          :width-offset="550"
          @clicked="openSelectedFilter"
          @cleared="clearFilters"
        />
        <b-dropdown
          ref="dateDropdown"
          menu-class="py-0 daterangepicker-menu"
          :disabled="isLoading"
          right
          class="mr-1"
          variant="outline-dark"
          size="sm"
          no-caret
        >
          <template #button-content>
            {{ dateRange }}
            <GjIcon
              name="Calendar"
              class="ml-50"
            />
            <b-button-close
              v-if="dateRangeSubmitted"
              class="ml-25 pb-25"
              @click.stop="clearDateRange"
            />
          </template>
          <div class="p-2">
            <date-range-picker
              ref="dateRangePicker"
              :ranges="predefinedRanges"
              default-range-select="today"
              :allow-compare="false"
              @submit="submitDateRange"
              @cancel="closeDropdown"
            />
          </div>
        </b-dropdown>
        <dropdown
          :title="$tk('ClientPortal.MasterTable.Filters')"
          class="mb-1 mr-1"
          :disabled="isLoading"
          :show-dropdown="showFilters"
          @change="toggleFilters"
        >
          <template #menu-items>
            <dropdown-submenu
              v-for="(filter, index) in model.filters"
              :key="filter.name + '-' + index"
              :input-id="filter.name"
              :text="filter.name"
              input-name="submenu"
              :checked="menu"
              :selected-filter="selectedFilter(filter.name)"
              @changeSelectedMenu="changeMenu"
            >
              <template #items>
                <dropdown-item v-if="filter.name !== 'Statuses' && filter.name !== 'Types'">
                  <b-form-input
                    v-model="model.serverParams.columnFilters[formatText(filter.name)]"
                    class="__dropdown-input"
                    :placeholder="$tk(`ClientPortal.Filters.Enter${filter.name}Value`)"
                    type="number"
                    :disabled="onChange"
                    @input="onInputChange(filter.name)"
                  />
                </dropdown-item>
                <template v-if="filter.name==='Statuses'">
                  <dropdown-item
                    v-for="(status, statusIndex) in model.statuses"
                    :key="status + '-' + statusIndex"
                    :label="status.text"
                    :filter-name="filter.name"
                    :value="status.value"
                    :menu-name="filter.name"
                    :selected="status.selected"
                    @change="includeOrExclude"
                  />
                </template>
                <template v-else-if="filter.name==='Types'">
                  <dropdown-item
                    v-for="(type, typeIndex) in model.types"
                    :key="type + '-' + typeIndex"
                    :label="type.text"
                    :filter-name="filter.name"
                    :value="type.value"
                    :menu-name="filter.name"
                    :selected="type.selected"
                    @change="includeOrExclude"
                  />
                </template>
                <template v-else>
                  <dropdown-item
                    v-for="(option, optionIndex) in filter.operators"
                    :key="option + '-' + optionIndex"
                    type="radio"
                    :label="option"
                    :filter-name="filter.name"
                    :selected="filter.selected"
                    :value="option"
                    :menu-name="filter.name"
                    @change="includeOrExclude"
                  />
                </template>
              </template>
              <template #footer>
                <dropdown-clear-item
                  :title="$tk('ClientPortal.Filters.Clear')"
                  @click="clearFilters(filter.name, false)"
                />
              </template>
            </dropdown-submenu>
            <dropdown-clear-item
              :title="$tk('ClientPortal.Filters.ClearAll')"
              @click="clearFilters"
            />
          </template>
        </dropdown>
        <SearchInputField
          :disabled="isLoading"
          :placeholder="$tk('Admin.Accounts.SearchByName')"
          @onInputSearch="onSearchChange"
        />
      </b-col>
      <b-card
        class="my-50 admin-table--management"
        no-body
      >
        <Table
          :data="allAds"
          :columns="adsColumns"
          :is-loading="isLoading"
          remote-mode
          class="admin-table__data"
          type-of-table="ads"
          :previewAd="previewAd"
          @changePage="changeCurrentPage"
          @onSortChange="sort"
        />

        <b-modal
          id="preview-ad-modal"
          v-model="showPreviewModal"
          title="Ad Preview"
          size="lg"
          hide-footer
        >
          <template v-if="adPreviewContent || ad">
            <div class="d-flex justify-content-center my-1">
              <a
                v-if="adPreviewType === AdPreviewType.NATIVE_AD"
                :href="ad.url"
                target="_blank"
              >
                <iframe
                  :src="ad.mediaUrl"
                  class="ad-preview-iframe"
                  :style="{
                    width: adPreviewSubType === AdPreviewSubType.UNDER_VIDEO_NATIVE_AD ? '728px' : adPreviewSubType === AdPreviewSubType.CHAT_MESSAGE_AD ? '400px' : '220px',
                    height: adPreviewSubType === AdPreviewSubType.UNDER_VIDEO_NATIVE_AD ? '75px' : adPreviewSubType === AdPreviewSubType.CHAT_MESSAGE_AD ? '140px' : '240px',
                    'pointer-events': 'none'
                  }"
                />
              </a>
              <img
                v-else-if="adPreviewType === AdPreviewType.DISPLAY_AD && adPreviewSubType === AdPreviewSubType.CATALOG_ITEM"
                :src="adPreviewContent"
                alt="Ad Preview"
                class="img-fluid"
                :style="{ maxWidth: '40%' }"
              >
              <img
                v-else-if="isImageAd"
                :src="adPreviewContent"
                alt="Ad Preview"
                class="img-fluid"
              >
              <video
                v-else-if="isVideoAd"
                controls
                class="img-fluid"
              >
                <source
                  :src="adPreviewContent"
                  type="video/mp4"
                >
                Your browser does not support the video tag.
              </video>
              <p
                v-else
              >Unsupported media type.
              </p>
            </div>
          </template>
          <p
            v-else
          >No preview available.
          </p>
        </b-modal>

        <b-modal
          id="approve-reject-ad-modal"
          ref="approveRejectAdModal"
          :title="modalConfirm.type == 1 ? $tk('ClientPortal.Admin.AreYouSureYouWantToApproveAd'):$tk('ClientPortal.Admin.AreYouSureYouWantToRejectAd')"
          centered
          no-close-on-backdrop
          no-enforce-focus
          footer="footer"
          @show="loadModalConfirm"
        >
          <div>
            {{ $tk("ClientPortal.Form.AdName") }}: <b>{{ modalConfirm.info.name }}</b> <br>
            {{ $tk("ClientPortal.Form.DailyBudget") }}: <b>{{ modalConfirm.info.dailyBudget }}</b> <br>
            {{ $tk("ClientPortal.Form.TotalBudget") }}: <b>{{ modalConfirm.info.totalBudget }}</b> <br>
            {{ $tk("ClientPortal.Admin.TypeOfAd") }}: <b>{{ modalConfirm.info.subtypeOfAd }}</b> <br>
          </div>

          <template #modal-footer="{ cancel, close }">
            <b-button
              class="m-0"
              size="sm"
              variant="outline-secondary"
              @click="cancel()"
            >
              {{ $tk('ClientPortal.Form.Actions.Cancel') }}
            </b-button>
            <b-button
              :variant="modalConfirm.type == 2 ? 'danger': 'primary'"
              class="m-0 ml-1 float-right"
              size="sm"
              :disabled="modalConfirm.loading"
              @click="modalConfirm.type == 1 ? updateAdStatus(modalConfirm.info, 'Active'): updateAdStatus(modalConfirm.info, 'Rejected'); close()"
            >
              <span v-if="modalConfirm.type == 1">
                {{ $tk("ClientPortal.Admin.Approve") }}
              </span>
              <span v-else>
                {{ $tk("ClientPortal.Admin.Reject") }}
              </span>
            </b-button>
          </template>
        </b-modal>
        <b-modal
          id="modal-no-budget"
          v-model="showNoBudgetModal"
          centered
          size="md"
          no-enforce-focus
          hide-footer
          header-class="pb-0"
          body-class="border-top-0"
        >
          <div class="d-flex flex-column text-center">
            <span class="mb-1">
              <GjIcon
                size="50"
                name="InfoAlt"
              />
            </span>
            <span class="col-8 m-auto">
              {{ $tk('ClientPortal.Admin.Ads.ThisAdCannotBeApprovedBecauseTheAccountBudgetIs') }} 0{{ $currencySymbol }}
            </span>
          </div>
        </b-modal>

      </b-card>
    </section>
  </div>
</template>

<script>
import {
  computed,
  onMounted,
  ref,
  reactive,
} from '@vue/composition-api'
import store from '@/store'
import Table from '@/components/Table.vue'
import AdsService from '@/api/admin/ads.service'
import SearchInputField from '@/components/SearchInputField.vue'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'
import Dropdown from '@/components/Dropdown.vue'
import DropdownItem from '@/components/DropdownItem.vue'
import DropdownSubmenu from '@/components/DropdownSubmenu.vue'
import DropdownClearItem from '@/components/DropdownClearItem.vue'
import FilterWrapper from '@/components/FilterWrapper.vue'
import DateRangePicker from '@/components/VueDateRangePicker'
import moment from 'moment'
import translation from '@/mixins/translate'
import onSortChange from '@/mixins/onSortChange'
import { debounce } from 'vue-debounce'

const AdStatus = {
  Active: 1,
  Inactive: 2,
  Pending: 3,
  Rejected: 4,
}

export default {
  components: {
    Table,
    SearchInputField,
    FilterWrapper,
    DropdownClearItem,
    DropdownItem,
    Dropdown,
    DropdownSubmenu,
    DateRangePicker,
    GjIcon,
  },
  mixins: [notification],
  setup(_, context) {
    const showPreviewModal = ref(false)
    const adPreviewContent = ref('')
    const adPreviewType = ref(null)
    const adPreviewSubType = ref(null)

    const ad = reactive({
      mediaUrl: '',
      url: '',
    })

    const AdPreviewType = {
      DISPLAY_AD: 1,
      VIDEO_AD: 2,
      NATIVE_AD: 3,
    }

    const AdPreviewSubType = {
      IMAGE_DISPLAY_AD: 1,
      HTML_DISPLAY_AD: 2,
      EXTERNAL_DISPLAY_AD: 3,
      BUMPER_VIDEO_AD: 4,
      SKIPPABLE_INSTREAM_VIDEO_AD: 5,
      PRODUCT_NATIVE_AD: 6,
      UNDER_VIDEO_NATIVE_AD: 7,
      SURVEY_DISPLAY_AD: 8,
      UNSKIPPABLE_INSTREAM_VIDEO_AD: 9,
      CHAT_MESSAGE_AD: 10,
      VIDEO_OUTSTREAM_AD: 11,
      COMPANION_AD: 12,
      CATALOG_ITEM: 13,
      VIDEO_OUTSTREAM: 14,
      ANCHOR: 15,
      PARALLAX: 16,
    }

    const adsColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.Id'),
        field: 'id',
        type: 'number',
        width: '100px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.Name'),
        field: 'name',
        width: '300px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.Status'),
        field: 'status',
        width: '100px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.DailyBudget'),
        field: 'dailyBudget',
        type: 'number',
        width: '105px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.TotalBudget'),
        field: 'totalBudget',
        type: 'number',
        width: '105px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.StartDate'),
        field: 'startDate',
        width: '175px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.EndDate'),
        field: 'endDate',
        width: '175px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.TypeOfAd'),
        field: 'subtypeOfAd',
        width: '100px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.Account'),
        field: 'accountName',
        width: '200px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Ads.CreatedAt'),
        field: 'insertedTime',
        type: 'date',
        dateInputFormat: 'dd/MM/yyyy HH:mm',
        dateOutputFormat: 'dd/MM/yyyy HH:mm',
        width: '225px !important',
      },
    ]
    const predefinedRanges = {
      today: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Today'),
        startDate: moment(),
        endDate: moment(),
      },
      yesterday: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Yesterday'),
        startDate: moment().subtract(1, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last7Days: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Last7Days'),
        startDate: moment().subtract(7, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      last30Days: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.Last30Days'),
        startDate: moment().subtract(30, 'days'),
        endDate: moment().subtract(1, 'days'),
      },
      lastMonth: {
        label: translation.methods.$tk('ClientPortal.PredefinedRanges.LastMonth'),
        startDate: moment().subtract(1, 'months').startOf('month'),
        endDate: moment().subtract(1, 'month').endOf('month'),
      },
    }
    const allAds = computed(() => store.getters.allAds)
    const model = computed(() => store.getters.getAdsModel)
    const { pageLength } = allAds.value
    const isLoading = ref(false)
    const menu = ref('')
    const showFilters = ref(false)
    const selectedFilters = reactive([])
    const dateRange = ref(translation.methods.$tk('ClientPortal.DateRange.SelectDateRange'))
    const dateRangeSubmitted = ref(false)
    const budgetSelected = ref(null)
    const showNoBudgetModal = ref(false)
    const onChange = ref(false)

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getAds')
      isLoading.value = false
    })

    const changeCurrentPage = async value => {
      model.value.serverParams.page = value
      await store.dispatch('getFilteredAds', model.value)
    }
    const sort = value => {
      onSortChange(value, model, 'getFilteredAds')
    }
    const onFilterChange = async () => {
      onChange.value = true
      const propertyObj = model.value.serverParams.propertyFilters
      if (propertyObj?.dailyBudget) {
        propertyObj.dailyBudget.value = propertyObj.dailyBudget.value.toString()
      }
      if (propertyObj?.totalBudget) {
        propertyObj.totalBudget.value = propertyObj.totalBudget.value.toString()
      }
      await store.dispatch('getFilteredAds', model.value)
      onChange.value = false
    }

    const changeMenu = newOpenedMenu => {
      menu.value === newOpenedMenu ? menu.value = '' : menu.value = newOpenedMenu
    }

    const formatText = name => name.charAt(0).toLowerCase() + name.slice(1)

    const modalConfirm = reactive({
      loading: true,
      type: 0,
      info: {},
    })

    const prependZero = value => {
      if (value.toString().length === 1) {
        return `0${value}`
      }
      return value
    }

    const formatDate = value => {
      const day = prependZero(value.getDate())
      const month = prependZero(value.getMonth() + 1)
      const year = prependZero(value.getFullYear())

      return `${day}/${month}/${year}`
    }

    const getDateFromMomentRange = (startMoment, endMoment) => {
      const startDate = new Date(startMoment)
      const endDate = new Date(endMoment)
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    }

    const updateSelectedFilters = (filter, count) => {
      const filterObj = selectedFilters.find(f => f.name === filter)
      if (filterObj) {
        if (count > 0) { filterObj.selectedFilters = count } else {
          const filterIndex = selectedFilters.findIndex(f => f.name === filter)
          selectedFilters.splice(filterIndex, 1)
        }
      } else {
        selectedFilters.push({ name: filter, selectedFilters: count })
      }
    }

    const updateStatuses = (filter, value) => {
      const { propertyFilters } = model.value.serverParams
      const selectedFilter = propertyFilters[filter]
      const optionIndex = selectedFilter.value.indexOf(value)
      let filterSelected = null
      if (filter === 'statuses') {
        filterSelected = model.value.statuses.find(status => status.value === value)
      } else if (filter === 'types') {
        filterSelected = model.value.types.find(type => type.value === value)
      }
      const { columnFilters } = model.value.serverParams
      if (optionIndex < 0) {
        selectedFilter.value.push(value)
        filterSelected.selected = true
        columnFilters[filter].push(value)
      } else {
        selectedFilter.value.splice(optionIndex, 1)
        filterSelected.selected = false
        columnFilters[filter].splice(optionIndex, 1)
        if (selectedFilter.value.length === 0 && columnFilters[filter].length === 0) {
          delete propertyFilters[filter]
          delete columnFilters[filter]
        }
      }
    }

    const updatePropertyFilter = (filter, value, operator) => {
      const { propertyFilters } = model.value.serverParams
      const selectedFilter = propertyFilters[filter]
      if (selectedFilter.operator === operator) {
        delete propertyFilters[filter]
      } else {
        selectedFilter.operator = operator
        selectedFilter.value = value
      }
    }

    const updatePropertyFilters = (filter, value, operator = null) => {
      const { propertyFilters } = model.value.serverParams
      const filterObject = formatText(filter)
      let count
      if (filterObject in propertyFilters) {
        if (filter === 'Statuses' || filter === 'Types') {
          updateStatuses(filterObject, value)
        } else {
          updatePropertyFilter(filterObject, value, operator)
        }
      } else {
        propertyFilters[filterObject] = {
          operator,
          property: filter,
          value: filter === 'Statuses' || filter === 'Types' ? [value] : value,
        }
        if (filter === 'Statuses' || filter === 'Types') {
          const { columnFilters } = model.value.serverParams
          columnFilters[filterObject] = []
          columnFilters[filterObject].push(value)
        }
      }
      if (propertyFilters[filterObject]) {
        count = filter === 'Statuses' || filter === 'Types' ? propertyFilters[filterObject].value.length : 1
      }
      updateSelectedFilters(filter, count)
    }

    const toggleFilters = (toogleFilter = null) => {
      menu.value = ''
      showFilters.value = toogleFilter ?? !showFilters.value
    }
    const includeOrExclude = (filter, option) => {
      if (filter === 'Statuses') {
        const statusSelected = model.value.statuses.find(status => status.value === option)
        statusSelected.selected === true ? statusSelected.selected = false : statusSelected.selected = true
        updatePropertyFilters(filter, option)
        onFilterChange()
      } else if (filter === 'Types') {
        const typeSelected = model.value.types.find(type => type.value === option)
        typeSelected.selected === true ? typeSelected.selected = false : typeSelected.selected = true
        updatePropertyFilters(filter, option)
        onFilterChange()
      } else {
        const selectedOption = model.value.filters.find(f => f.name === filter)
        const { columnFilters } = model.value.serverParams
        selectedOption.selected === option ? selectedOption.selected = '' : selectedOption.selected = option
        updatePropertyFilters(filter, columnFilters[formatText(filter)], option)
        if (!columnFilters[formatText(filter)].toString()) { return }
        onFilterChange()
      }
    }

    const clearFilters = (filter = null, closeAllDropdowns = true) => {
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      const { statuses } = model.value
      const { types } = model.value
      const { filters } = model.value
      if (!filter) {
        selectedFilters.splice(0, selectedFilters.length)
        Object.keys(propertyFilters).forEach(propertyFilter => { delete propertyFilters[propertyFilter] })
        Object.keys(columnFilters).forEach(columnFilter => { columnFilters[columnFilter] = null })
        statuses.forEach(status => { status.selected = false })
        types.forEach(type => { type.selected = false })
        filters.find(f => f.name === 'DailyBudget').selected = ''
        filters.find(f => f.name === 'TotalBudget').selected = ''
        toggleFilters(false)
      } else {
        const selectedFilter = model.value.filters.find(f => f.name === filter)
        const includedFilterIndex = selectedFilters.findIndex(f => f.name === filter)
        const filterObject = formatText(filter)
        columnFilters[filterObject] = null
        selectedFilters.splice(includedFilterIndex, 1)
        selectedFilter.selected = ''
        delete propertyFilters[filterObject]
        if (filter === 'Statuses') { statuses.forEach(status => { status.selected = false }) }
        if (filter === 'Types') { types.forEach(type => { type.selected = false }) }
        if (closeAllDropdowns) { toggleFilters(false) }
      }
      onFilterChange()
      menu.value = ''
    }

    const onInputChange = debounce(filter => {
      const filterObject = formatText(filter)
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      if (filterObject in propertyFilters) {
        propertyFilters[filterObject].value = columnFilters[filterObject]
        if (propertyFilters[filterObject].value === '') {
          budgetSelected.value = propertyFilters[filterObject].operator
          delete propertyFilters[filterObject]
        }
      } else if (budgetSelected.value !== null) {
        propertyFilters[formatText(filter)] = {
          operator: budgetSelected.value,
          property: filter,
          value: columnFilters[filterObject],
        }
      } else { return }
      onFilterChange()
    }, 500)

    const loadModalConfirm = () => {
      const data = computed(() => store.getters.modalConfirm)
      modalConfirm.loading = data.value.loading
      modalConfirm.type = data.value.type
      modalConfirm.info = data.value.info
    }

    const previewAd = async adId => {
      try {
        const adInfo = allAds.value.rows.find(adItem => adItem.id === adId)
        if (!adInfo) {
          throw new Error('Ad information not found')
        }

        const response = await AdsService.getPreviewAd(adId, adInfo)
        console.log('response', response)

        adPreviewContent.value = response.mediaUrl
        adPreviewType.value = response.type
        adPreviewSubType.value = response.subType

        if (adPreviewType.value === AdPreviewType.NATIVE_AD) {
          let content = response.nativeAd.htmlContent
          response.nativeAd.customProperties.forEach(prop => {
            const regex = new RegExp(`{{${prop.name}}}`, 'g')
            content = content.replace(regex, prop.value || '')
          })

          content = content.replace(/{{\w+}}/g, '')
          const blob = new Blob([content], { type: 'text/html' })
          const url = URL.createObjectURL(blob)

          ad.mediaUrl = url
          ad.url = response.nativeAd.url
        } else {
          ad.mediaUrl = response.mediaUrl
        }
        showPreviewModal.value = true
      } catch (error) {
        notification.methods.error(error.message)
      }
    }

    const isImageAd = computed(() => [
      AdPreviewSubType.IMAGE_DISPLAY_AD,
      AdPreviewSubType.SURVEY_DISPLAY_AD,
      AdPreviewSubType.CATALOG_ITEM,
    ].includes(adPreviewSubType.value))

    const isVideoAd = computed(() => [
      AdPreviewSubType.BUMPER_VIDEO_AD,
      AdPreviewSubType.SKIPPABLE_INSTREAM_VIDEO_AD,
      AdPreviewSubType.UNSKIPPABLE_INSTREAM_VIDEO_AD,
      AdPreviewSubType.VIDEO_OUTSTREAM,
      AdPreviewSubType.COMPANION_AD,
      AdPreviewSubType.VIDEO_OUTSTREAM_AD,
    ].includes(adPreviewSubType.value))

    const updateAdStatus = async (filter, status) => {
      const statusId = AdStatus[status]
      try {
        const response = await AdsService.updateAdStatus(filter.id, statusId)
        if (response.saved) {
          notification.methods.success(response.message)
        } else {
          showNoBudgetModal.value = true
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        await store.dispatch('getFilteredAds', model.value)
      }
    }
    const closeDropdown = () => {
      context.refs.dateDropdown.hide(true)
    }

    const clearDateRange = async () => {
      model.value.serverParams.dateRange = null
      dateRangeSubmitted.value = false
      context.refs.dateRangePicker.selectRange('today')
      dateRange.value = 'Select date range'
      await store.dispatch('getFilteredAds', model.value)
    }

    window.onclick = e => {
      if (!e.path.find(x => x.className?.includes('dropdown'))) {
        toggleFilters(false)
        menu.value = ''
      }
    }

    const submitDateRange = async range => {
      const { startDate, endDate } = range
      dateRangeSubmitted.value = true
      dateRange.value = getDateFromMomentRange(startDate, endDate)
      if (model.value.serverParams.dateRange !== dateRange.value) {
        model.value.serverParams.dateRange = dateRange.value
      }
      await store.dispatch('getFilteredAds', model.value)

      closeDropdown()
    }

    const onSearchChange = async value => {
      model.value.serverParams.name = value
      if (!value) {
        await store.dispatch('getFilteredAds', model.value)
      }
      await store.dispatch('getFilteredAds', model.value)
    }

    const selectedFilter = name => {
      const filter = selectedFilters.findIndex(f => f.name === name)
      if (filter > -1) return true
      return false
    }

    const openSelectedFilter = filterName => {
      toggleFilters(true)
      menu.value = filterName
    }

    return {
      allAds,
      isLoading,
      changeCurrentPage,
      pageLength,
      updateAdStatus,
      modalConfirm,
      loadModalConfirm,
      onInputChange,
      clearFilters,
      includeOrExclude,
      changeMenu,
      selectedFilters,
      showFilters,
      toggleFilters,
      model,
      menu,
      predefinedRanges,
      submitDateRange,
      dateRange,
      closeDropdown,
      clearDateRange,
      dateRangeSubmitted,
      formatText,
      onSearchChange,
      adsColumns,
      sort,
      selectedFilter,
      openSelectedFilter,
      showNoBudgetModal,
      onChange,
      showPreviewModal,
      adPreviewType,
      adPreviewSubType,
      adPreviewContent,
      previewAd,
      isImageAd,
      isVideoAd,
      ad,
      AdPreviewType,
      AdPreviewSubType,
    }
  },
}
</script>

<style>
.ad-image-preview {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  height: 500px;
  width: 800px;
}

.ad-preview-iframe {
  border: none;
  pointer-events: none;
}
</style>
